import React, { useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router";
import { useQuery } from "@apollo/client/react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import {
  GET_SHIPMENTS_QUERY,
  TRANSFER_SHIPMENT_TARGET_QUERY,
} from "../../../constants/graphql/queries";
import TransferShipmentWizard from "./TransferShipmentWizard";
import InboundTable from "./InboundTable";
import InboundTableSkeleton from "./InboundTableSkeleton";
import InboundNoResults from "./InboundNoResults";
import BillingTotalsModal, {
  BILLING_TOTALS_PARAMS,
} from "../billing/BillingTotalsModal";
import BillingLineItemsExportModal, {
  BILLING_LINE_ITEMS_EXPORT_PARAMS,
} from "../billing/BillingLineItemsExportModal";
import { useProject } from "../../../contexts/project";
import { extractBillingTypes } from "../../../utils/billingHelpers";
import useRoles from "../../../hooks/user/useRoles";
import useProjectMovewareLink from "src/hooks/useProjectMovewareLink";

const initialPage = 1;
const limit = 50;
const HAS_OSD_PARAM = "hasOsd";
const OSD_SEARCH_VALUE = "hasOsd=true";
const billingTotalsParams = Object.values(BILLING_TOTALS_PARAMS);
const billingLineItemsExportParams = Object.values(BILLING_LINE_ITEMS_EXPORT_PARAMS);

const isOsdSearch = value => (value || "").trim() === OSD_SEARCH_VALUE;

const resolveSearch = (value, hasOsdFromUrl = false) => {
  if (isOsdSearch(value) || (hasOsdFromUrl && !value)) {
    return { search: "", hasOsd: true };
  }

  return { search: value || "", hasOsd: false };
};

const buildShipmentVariables = ({ projectId, clientId, search, page, hasOsd }) => ({
  ...(projectId ? { projectId } : {}),
  ...(clientId ? { clientId } : {}),
  search,
  limit,
  page,
  shipment_id_order: "asc",
  ...(hasOsd ? { hasOsd: true } : {}),
});

const hasApprovalEmails = emailList => !!emailList?.trim();

export default function Inbound() {
  const navigate = useNavigate();
  const { projectId, clientId } = useParams();
  const location = useLocation();
  const currentProject = useProject();
  const showMovewareDetails = useProjectMovewareLink(projectId);
  const isAdminOrManager = useRoles(["admin", "manager"]);
  const canViewBilling = isAdminOrManager && currentProject?.billing;
  const currentClientId = clientId || currentProject?.id;
  const queryParams = new URLSearchParams(location.search);
  const hasOsdFromUrl = queryParams.get(HAS_OSD_PARAM) === "true";
  const initialSearch = queryParams.get("inboundSearch") || "";
  const resolvedInitialSearch = resolveSearch(initialSearch, hasOsdFromUrl);
  const hasOsd = resolvedInitialSearch.hasOsd;
  const initialInput = hasOsd ? OSD_SEARCH_VALUE : initialSearch;

  const [searchInput, setSearchInput] = useState(initialInput);
  const [page, setPage] = useState(initialPage);
  const [moreOptionsAnchorEl, setMoreOptionsAnchorEl] = useState(null);
  const [variables, setVariables] = useState(() =>
    buildShipmentVariables({
      projectId,
      search: resolvedInitialSearch.search,
      page: initialPage,
      hasOsd,
    })
  );

  const transferShipmentId = useMemo(
    () => new URLSearchParams(location.search).get("transferShipmentId"),
    [location.search]
  );
  const { data, loading } = useQuery(GET_SHIPMENTS_QUERY, {
    variables,
  });

  const shipments = data?.shipments?.collection || [];
  const listedTransferShipment = transferShipmentId
    ? shipments.find(item => item.id === transferShipmentId)
    : null;
  const { data: transferShipmentData } = useQuery(TRANSFER_SHIPMENT_TARGET_QUERY, {
    variables: { id: transferShipmentId },
    skip: !transferShipmentId || !!listedTransferShipment,
    fetchPolicy: "cache-first",
  });
  const transferShipment = listedTransferShipment || transferShipmentData?.shipment;
  const metadata = data?.shipments?.metadata || { totalCount: 0, totalPages: 0, currentPage: 1 };
  const showTotalWeight = extractBillingTypes(
    currentProject?.billingTypes?.inbound,
    currentProject?.billingType
  )?.types?.includes("Hundredweight");
  const displayCustomerNotified =
    hasApprovalEmails(currentProject?.approvedEmailSendList) ||
    shipments.some(item =>
      hasApprovalEmails(item?.project?.approvedEmailSendList) ||
      hasApprovalEmails(item?.costCenter?.approvedEmailSendList)
    );

  const persistSearchParams = ({ searchValue = searchInput } = {}) => {
    const { search, hasOsd: nextHasOsd } = resolveSearch(searchValue);
    const params = new URLSearchParams(location.search);
    if (search) {
      params.set("inboundSearch", search);
    } else {
      params.delete("inboundSearch");
    }
    if (nextHasOsd) {
      params.set(HAS_OSD_PARAM, "true");
    } else {
      params.delete(HAS_OSD_PARAM);
    }
    navigate({ search: params.toString() }, { replace: true });
  };

  const applySearch = (rawValue, { clientId: nextClientId } = {}) => {
    const { search, hasOsd: nextHasOsd } = resolveSearch(rawValue);
    setPage(1);
    setVariables(
      buildShipmentVariables({
        projectId: nextClientId ? undefined : projectId,
        clientId: nextClientId,
        search,
        page: 1,
        hasOsd: nextHasOsd,
      })
    );
    persistSearchParams({ searchValue: rawValue });
  };

  const handleSearch = () => {
    applySearch(searchInput);
  };

  const handleKeyPress = e => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleSearchAll = () => {
    const { search, hasOsd: nextHasOsd } = resolveSearch(searchInput);
    setVariables(
      buildShipmentVariables({
        clientId: currentClientId,
        search,
        page: 1,
        hasOsd: nextHasOsd,
      })
    );
  };

  const handleClear = () => {
    setSearchInput("");
    applySearch("");
  };

  const handleToggleOsdFilter = () => {
    const nextValue = hasOsd ? "" : OSD_SEARCH_VALUE;
    setMoreOptionsAnchorEl(null);
    setSearchInput(nextValue);
    applySearch(nextValue);
  };

  const handleToggle = shipment => {
    const params = new URLSearchParams(location.search);
    params.set("step", "shipment");
    params.set("receiving", "true");
    params.set("editShipment", "true");
    params.set("shipmentId", shipment.shipmentId);
    params.set("id", shipment.id);
    navigate({ search: params.toString() });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    setVariables(v => ({ ...v, page: newPage }));
  };

  const handleOpenBillingTotals = () => {
    const params = new URLSearchParams(location.search);
    params.set(BILLING_TOTALS_PARAMS.open, "true");
    params.set(BILLING_TOTALS_PARAMS.step, "0");
    navigate({ search: params.toString() });
    setMoreOptionsAnchorEl(null);
  };

  const handleOpenExportModal = () => {
    const params = new URLSearchParams(location.search);
    params.set(BILLING_LINE_ITEMS_EXPORT_PARAMS.open, "true");
    params.set(BILLING_LINE_ITEMS_EXPORT_PARAMS.step, "0");
    navigate({ search: params.toString() });
    setMoreOptionsAnchorEl(null);
  };

  const handleCloseBillingTotals = () => {
    const params = new URLSearchParams(location.search);
    billingTotalsParams.forEach(param => params.delete(param));
    navigate({ search: params.toString() });
  };

  const handleCloseExportModal = () => {
    const params = new URLSearchParams(location.search);
    billingLineItemsExportParams.forEach(param => params.delete(param));
    navigate({ search: params.toString() });
  };

  const headerAction = (
    <>
      <Tooltip title="More Options">
        <Button
          size="small"
          color="inherit"
          aria-label="Inbound more options"
          onClick={event => setMoreOptionsAnchorEl(event.currentTarget)}
          endIcon={<MoreVertIcon fontSize="small" />}
        >
          More Options
        </Button>
      </Tooltip>
      <Menu
        anchorEl={moreOptionsAnchorEl}
        open={Boolean(moreOptionsAnchorEl)}
        onClose={() => setMoreOptionsAnchorEl(null)}
      >
        <MenuItem selected={hasOsd} onClick={handleToggleOsdFilter}>
          OS&D Shipments
        </MenuItem>
        {canViewBilling && (
          <MenuItem onClick={handleOpenBillingTotals}>
            Inbound Billing Totals
          </MenuItem>
        )}
        {canViewBilling && (
          <MenuItem onClick={handleOpenExportModal}>
            Itemized Billing Excel Export
          </MenuItem>
        )}
      </Menu>
    </>
  );

  return (
    <Paper>
      <Box
        p={2}
        pb={{ xs: 0, sm: 2 }}
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        flexDirection={{ xs: "column", sm: "row" }}
        gap={1}
      >
        <Typography fontWeight={500}>Inbound Shipments</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {hasOsd && (
            <Chip
              label="OS&D"
              size="small"
              variant="outlined"
              onDelete={handleToggleOsdFilter}
            />
          )}
          <TextField
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
            size="small"
            placeholder="Search"
            InputProps={{
              endAdornment: searchInput ? (
                <IconButton onClick={handleClear} size="small">
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton onClick={handleSearch} size="small">
                  <SearchIcon color="action" />
                </IconButton>
              ),
            }}
          />
        </Box>
      </Box>
      {loading ? (
        <InboundTableSkeleton />
      ) : variables.projectId && variables.search && shipments.length === 0 ? (
        <Box>
          <Box px={2} display="flex" justifyContent="flex-end">
            {headerAction}
          </Box>
          <InboundNoResults onSearchAll={handleSearchAll} />
        </Box>
      ) : shipments.length === 0 ? (
        <Box p={2} pb={5} display="flex" justifyContent="space-between" alignItems="center">
          <Typography color="text.secondary">No Inbound Shipments Found</Typography>
          {headerAction}
        </Box>
      ) : (
        <Box p={2}>
          <InboundTable
            list={shipments}
            variables={variables}
            onEdit={handleToggle}
            page={metadata.currentPage}
            totalPages={metadata.totalPages}
            totalCount={metadata.totalCount}
            displayCustomerNotified={displayCustomerNotified}
            showTotalWeight={showTotalWeight}
            showBillingCharge={canViewBilling}
            headerAction={headerAction}
            compactActions
            showMovewareDetails={showMovewareDetails}
          />
          {metadata.totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={metadata.totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </Box>
      )}
      {canViewBilling && queryParams.get(BILLING_TOTALS_PARAMS.open) === "true" && (
        <BillingTotalsModal
          projectId={projectId}
          onClose={handleCloseBillingTotals}
        />
      )}
      {canViewBilling && queryParams.get(BILLING_LINE_ITEMS_EXPORT_PARAMS.open) === "true" && (
        <BillingLineItemsExportModal
          projectId={projectId}
          onClose={handleCloseExportModal}
        />
      )}
      {transferShipment && <TransferShipmentWizard shipment={transferShipment} />}
    </Paper>
  );
}
